import { Injectable, Injector, inject } from '@angular/core';
import { CoreBackendIDs, CoreHttpClient, CORE_BACKEND } from '@bepatient/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Factsheet,
  FactsheetItemBullet,
  FactsheetItemButton,
  FactsheetItemImage,
  FactsheetItems,
  FactsheetItemText,
  FactsheetItemTitle,
  FactsheetItemTypes,
  FactsheetItemVideo,
  FactsheetImageSubTypes
} from './factsheet.models';

/**
 * Service responsable des calls API pour les fiches partiques
 */
@Injectable()
export class FactsheetApiService {
  private http = inject(CoreHttpClient);
  private injector = inject(Injector);

  /**
   * @ignore
   */

  SMART = this.injector.get(CORE_BACKEND) === CoreBackendIDs.SMART;

  /**
   * Charge une fiche pratique et fait un POST vide sur le même href
   *
   * @param href Le href de la fiche pratique à charger
   */
  loadFactsheet(href: string): Observable<Factsheet> {
    return this.http.get(href, { withMobiCredentials: true }).pipe(
      // return this.mockedFactsheet().pipe(
      map(res => this.factsheetParser(res))
    );
  }

  public saveFactsheet(id): Observable<any> {
    const data = {
      is_form_fully_completed: true
    };
    return this.http
      .put(`/rest/contents/forms/${id}/answers/${id}`, data, {
        withMobiCredentials: true
      })
      .pipe(map(res => res));
  }

  /**
   * Parse une fiche pratique envoyée par MOBI
   *
   * @param data Les données retournées par MOBI
   */
  private factsheetParser(data: any): Factsheet {
    if (this.SMART) {
      data = data.payload;
    }

    const convertType = (item: any): FactsheetItems => {
      switch (item.type) {
        case 'title':
          const itemTitle: FactsheetItemTitle = {
            type: FactsheetItemTypes.TITLE,
            title: item.display_title
              ? item.display_title || item.title || ''
              : ''
          };
          return itemTitle;
        case 'html':
          const itemText: FactsheetItemText = {
            type: FactsheetItemTypes.TEXT,
            title: item.display_title
              ? item.title || item.display_title || ''
              : '',
            image: item.image || '',
            button: {
              label: item.button_label || '',
              href: item.button_url || ''
            },
            text: item.text || '',
            items: item.items || []
          };
          return itemText;
        case 'bullet':
          const itemBullet: FactsheetItemBullet = {
            type: FactsheetItemTypes.BULLET,
            title: item.display_title
              ? item.display_title || item.title || ''
              : '',
            image: item.image || '',
            imageAlign: item.display_image_side || 'left',
            text: item.text || ''
          };
          return itemBullet;
        case 'image':
          let subType: FactsheetImageSubTypes = void 0;
          switch (item.image_format) {
            case 'svg':
              subType = FactsheetImageSubTypes.SVG;
              break;
            case 'png':
              subType = FactsheetImageSubTypes.PNG;
              break;
            case 'gif':
              subType = FactsheetImageSubTypes.GIF;
              break;
            case 'jpeg':
              subType = FactsheetImageSubTypes.JPEG;
              break;
            case 'lottie_file':
              subType = FactsheetImageSubTypes.LOTTIE_FILE;
              break;
          }
          const itemImage: FactsheetItemImage = {
            type: FactsheetItemTypes.IMAGE,
            subType: subType || void 0,
            url: item.image || ''
          };
          return itemImage;
        case 'button':
          const itemButton: FactsheetItemButton = {
            type: FactsheetItemTypes.BUTTON,
            label: item.button_label || '',
            href: item.button_url || ''
          };
          return itemButton;
        case 'video':
          const itemVideo: FactsheetItemVideo = {
            type: FactsheetItemTypes.VIDEO,
            videoType: item.video_type,
            url: item.url || item.video_url || ''
          };
          return item.video_type === 'youtube' || item.video_type === 'vimeo'
            ? itemVideo
            : void 0;
        default:
          return void 0;
      }
    };

    return {
      abspath: (data.meta && data.meta.abspath) || '',
      color: (data.meta && data.meta.color) || data.color || '',
      title: (data.meta && data.meta.title) || data.title || '',
      image: (data.meta && data.meta.thumbnail) || data.image || '',
      items: (data.items || [] || data.items)
        .map(item => convertType(item))
        .filter(item => !!item)
    };
  }

  /**
   * @ignore
   */
  private mockedFactsheet(): Observable<Factsheet> {
    return of<Factsheet>({
      color: '#fc907b',
      title: 'Alimentation',
      image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
      items: [
        {
          type: FactsheetItemTypes.TITLE,
          title: 'La base d une alimentation saine'
        },
        {
          type: FactsheetItemTypes.BULLET,
          text: 'Bien manger, c est le début de la bonne santé. En plus, ça permet d avoir des forces',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          imageAlign: 'left'
        },
        {
          type: FactsheetItemTypes.BULLET,
          text: 'Bien manger, c est le début de la bonne santé. En plus, ça permet d avoir des forces',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          imageAlign: 'right'
        },
        {
          type: FactsheetItemTypes.BULLET,
          title: 'Faire attention',
          text: 'Bien manger, c est le début de la bonne santé. En plus, ça permet d avoir des forces',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          imageAlign: 'left'
        },
        {
          type: FactsheetItemTypes.BULLET,
          title: 'Faire attention',
          text: 'Bien manger, c est le début de la bonne santé. En plus, ça permet d avoir des forces',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          imageAlign: 'right'
        },
        {
          type: FactsheetItemTypes.TEXT,
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          button: {
            label: 'Je suis un bouton',
            href: 'https://www.google.com/'
          },
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          title: 'Une alimentation saine',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          title: 'Une alimentation saine',
          button: {
            label: 'Je suis un bouton',
            href: 'https://www.google.com/'
          },
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          button: {
            label: 'Je suis un bouton',
            href: 'https://www.google.com/'
          },
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.TEXT,
          title: 'Une alimentation saine',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          button: {
            label: 'Je suis un bouton',
            href: 'https://www.google.com/'
          },
          text:
            // tslint:disable-next-line:max-line-length
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin eget pulvinar purus. Ut tincidunt malesuada ultricies. Pellentesque eu nibh id nisl auctor placerat. Cras cursus augue sit amet dui euismod, in blandit arcu sollicitudin. Mauris iaculis euismod rutrum. Pellentesque id libero sed quam scelerisque finibus. Mauris commodo arcu sed dui pellentesque, ut porta arcu dignissim. Nulla porta, massa luctus finibus scelerisque, eros tellus porttitor justo, et efficitur est est a leo. Morbi nec ultrices tortor, id vestibulum lorem. Integer arcu dui, feugiat non purus ac, tristique vehicula purus. Ut a magna turpis. Quisque a sagittis odio. In in gravida nibh, hendrerit finibus massa. Fusce quis pulvinar nunc. Sed nec porta ipsum. Etiam ut nisi nec felis sagittis sollicitudin.'
        },
        {
          type: FactsheetItemTypes.BUTTON,
          label: 'Je suis un bouton',
          href: 'https://www.google.com/'
        },
        {
          type: FactsheetItemTypes.IMAGE,
          subType: FactsheetImageSubTypes.SVG,
          url: 'https://dev-mobi-julien.bepatientsolutions.com/images/30'
        },
        {
          type: FactsheetItemTypes.VIDEO,
          videoType: 'vimeo',
          url: 'https://vimeo.com/138706287'
        },
        {
          type: FactsheetItemTypes.VIDEO,
          videoType: 'youtube',
          url: 'https://youtu.be/BVzK_p6llJw'
        }
      ]
    });
  }
}
